import { PAYMENT_FIELDS } from 'constants/formFields';
import {
  CHECKOUT_RECEIVE_PAYMENTS,
  CHECKOUT_RESET_TO_DEFAULT_STATE,
  NEW_PAYMENT_PRIMARY,
  RECEIVE_FIELD_VALIDATION_ERRORS,
  RECEIVE_NEW_PAYMENT_FIELD,
  SET_CHECKOUT_PAYMENTS,
  SET_CHECKOUT_PAYMENTS_LOADING,
  VALIDATE_PAYMENT_FIELDS
} from 'constants/reduxActions';
import { PAYMENT_MESSAGES } from 'constants/errorMessages';
import { formatCreditCardExpiration } from 'helpers/MyAccountUtils';
import { isValidCreditCardNumber } from 'helpers/PaymentUtils';
import { EXPIRATION_MM_YYYY } from 'common/regex';
import { getRequestFieldDetails, getRequestFieldError, hasValue } from 'reducers/checkout/common';

const newPayment = {
  cc: '',
  expiration: '',
  expirationMonth: '',
  expirationYear: '',
  name: ''
};

const defaultState = {
  new: newPayment,
  saved: [],
  isNewPrimary: true,
  isLoaded: false,
  loadingMessage: ''
};

export function paymentTypes(state = defaultState, action = {}) {
  const {
    data,
    fieldKey,
    fieldValue,
    isNewPrimary,
    loadingMessage,
    type,
    requestExtraInformation
  } = action;

  switch (type) {
    case SET_CHECKOUT_PAYMENTS: {
      const { paymentOptions } = data;
      const { saved: stateSavedPayments } = state;

      const savedPaymentTypes = paymentOptions && paymentOptions.paymentInstruments
        ? paymentOptions.paymentInstruments
        : stateSavedPayments;

      return { ...state, isLoaded: true, loadingMessage: '', saved: savedPaymentTypes, new: newPayment, isNewPrimary: true };
    }

    case RECEIVE_NEW_PAYMENT_FIELD: {
      const newPayment = { ...state.new, [fieldKey]: fieldValue };
      let newState = { ...state, new: newPayment };
      newState = buildPaymentExpiration(newState);
      return newState;
    }

    case CHECKOUT_RECEIVE_PAYMENTS: {
      return { ...state, saved: data.paymentInstruments, loadingMessage: '' };
    }

    case SET_CHECKOUT_PAYMENTS_LOADING: {
      return { ...state, loadingMessage };
    }

    case NEW_PAYMENT_PRIMARY: {
      return { ...state, isNewPrimary };
    }

    case RECEIVE_FIELD_VALIDATION_ERRORS:
    {
      let { new: newPayment } = state;

      requestExtraInformation.forEach(fieldError => {
        const { form, formField } = getRequestFieldDetails(fieldError.fieldName);
        if (form === 'payment') {
          const errorMessage = getRequestFieldError(fieldError.id);
          const paymentErrors = newPayment.errors ? newPayment.errors : {};
          newPayment = { ...newPayment, errors: { ...paymentErrors, [formField.fieldName]: [errorMessage] } };
        }
      });

      return { ...state, new: newPayment };
    }

    case VALIDATE_PAYMENT_FIELDS: {
      let newState = state;
      Object.keys(PAYMENT_FIELDS).forEach(field => {
        newState = validatePaymentField(newState, PAYMENT_FIELDS[field]);
      });
      return newState;
    }

    case CHECKOUT_RESET_TO_DEFAULT_STATE: {
      return defaultState;
    }

    default:
      return state;
  }
}

function validatePaymentField(state, fieldKey) {
  const newError = [];
  const { new: payment } = state;
  const key = typeof fieldKey === 'string' ? fieldKey : fieldKey.fieldName;

  switch (key) {
    case PAYMENT_FIELDS.CC_EXPIRATION_YEAR.fieldName:
    case PAYMENT_FIELDS.CC_EXPIRATION_MONTH.fieldName:
    {
      return validatePaymentField(state, PAYMENT_FIELDS.CC_EXPIRATION.fieldName);
    }
    case PAYMENT_FIELDS.CC_EXPIRATION.fieldName:
    {
      const field = PAYMENT_FIELDS.CC_EXPIRATION.fieldName;
      if (!hasValue(payment, field)) {
        newError.push(PAYMENT_MESSAGES.missing_value[field]);
        break;
      }
      if (!validateCardExpirationFormat(payment[field])) {
        newError.push(PAYMENT_MESSAGES.cc_expiration_bad_format);
        break;
      }
      if (!validateCardIsCurrent(payment[field])) {
        newError.push(PAYMENT_MESSAGES.cc_expired);
      }
      break;
    }
    case PAYMENT_FIELDS.CC.fieldName:
    {
      const field = PAYMENT_FIELDS.CC.fieldName;
      if (payment.paymentInstrumentId) { // editing a payment, so full CC is not available
        break;
      }
      if (!hasValue(payment, field)) {
        newError.push(PAYMENT_MESSAGES.missing_value[field]);
        break;
      }
      if (!isValidCreditCardNumber(payment[field])) {
        newError.push(PAYMENT_MESSAGES.cc_bad_format);
      }
      break;
    }
    case PAYMENT_FIELDS.NAME_ON_CARD.fieldName:
    {
      const field = PAYMENT_FIELDS.NAME_ON_CARD.fieldName;
      if (!hasValue(payment, field)) {
        newError.push(PAYMENT_MESSAGES.missing_value[field]);
      }
      break;
    }
  }

  const paymentErrors = payment ? payment.errors : {};
  const newPayment = { ...payment, errors: { ...paymentErrors, [key]: newError.length ? newError : null } };
  return { ...state, new: newPayment };
}

function validateCardIsCurrent(expiration) {
  const { expirationMonth, expirationYear } = formatCreditCardExpiration(expiration);
  const currDate = new Date();
  const expDate = new Date();
  expDate.setFullYear(expirationYear, expirationMonth, 1); // will use 1st day of month after expiration
  return expDate > currDate;
}

function validateCardExpirationFormat(expiration) {
  // format: dd dddd (mm yyyy)
  return expiration.length === 7 && expiration.match(EXPIRATION_MM_YYYY);
}

function buildPaymentExpiration(state) {
  const { new: storeNewPayment } = state;
  const { expirationMonth, expirationYear } = storeNewPayment;
  const newPayment = { ...storeNewPayment, expiration: `${expirationMonth} ${expirationYear}` };

  return { ...state, new: newPayment };
}
